import { forwardRef, type HTMLAttributes, type KeyboardEvent } from 'react';
import styles from './EmojiRating.module.css';

export type EmojiRatingSize = 'sm' | 'md' | 'lg';

/** Rating option with emoji and accessible label */
interface RatingOption {
  value: number;
  emoji: string;
  label: string;
}

const RATING_OPTIONS: RatingOption[] = [
  { value: 1, emoji: '🤬', label: 'Terrible' },
  { value: 2, emoji: '☹️', label: 'Poor' },
  { value: 3, emoji: '😐', label: 'Okay' },
  { value: 4, emoji: '😊', label: 'Good' },
  { value: 5, emoji: '🎉', label: 'Amazing' },
];

export interface EmojiRatingProps extends Omit<HTMLAttributes<HTMLDivElement>, 'onChange'> {
  /** Currently selected rating value (1-5) or null if none selected */
  value: number | null;
  /** Callback when a rating is selected */
  onChange: (rating: number) => void;
  /** Whether the rating is readonly/non-interactive */
  readonly?: boolean;
  /** Size of the emoji buttons */
  size?: EmojiRatingSize;
}

/**
 * EmojiRating component for logging performance ratings.
 * Displays 5 emoji options from terrible (1) to amazing (5).
 *
 * @example
 * ```tsx
 * <EmojiRating
 *   value={3}
 *   onChange={(rating) => console.log('Selected:', rating)}
 * />
 *
 * <EmojiRating
 *   value={5}
 *   onChange={() => {}}
 *   readonly
 *   size="sm"
 * />
 * ```
 */
export const EmojiRating = forwardRef<HTMLDivElement, EmojiRatingProps>(
  (
    {
      value,
      onChange,
      readonly = false,
      size = 'md',
      className,
      ...props
    },
    ref
  ) => {
    const handleSelect = (rating: number) => {
      if (!readonly) {
        onChange(rating);
      }
    };

    const handleKeyDown = (e: KeyboardEvent<HTMLButtonElement>, rating: number) => {
      if (readonly) return;

      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        onChange(rating);
      } else if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
        e.preventDefault();
        const nextRating = Math.min(rating + 1, 5);
        const nextButton = e.currentTarget.parentElement?.querySelector(
          `[data-rating="${nextRating}"]`
        ) as HTMLButtonElement | null;
        nextButton?.focus();
      } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
        e.preventDefault();
        const prevRating = Math.max(rating - 1, 1);
        const prevButton = e.currentTarget.parentElement?.querySelector(
          `[data-rating="${prevRating}"]`
        ) as HTMLButtonElement | null;
        prevButton?.focus();
      }
    };

    const containerClassNames = [
      styles.container,
      styles[size],
      readonly && styles.readonly,
      className,
    ]
      .filter(Boolean)
      .join(' ');

    return (
      <div
        ref={ref}
        className={containerClassNames}
        role="radiogroup"
        aria-label="Performance rating"
        {...props}
      >
        {RATING_OPTIONS.map((option) => {
          const isSelected = value === option.value;

          const buttonClassNames = [
            styles.option,
            isSelected && styles.selected,
          ]
            .filter(Boolean)
            .join(' ');

          return (
            <button
              key={option.value}
              type="button"
              className={buttonClassNames}
              onClick={() => handleSelect(option.value)}
              onKeyDown={(e) => handleKeyDown(e, option.value)}
              disabled={readonly}
              role="radio"
              aria-checked={isSelected}
              aria-label={`${option.label} - ${option.value} out of 5`}
              data-rating={option.value}
              tabIndex={readonly ? -1 : (isSelected || (value === null && option.value === 1)) ? 0 : -1}
            >
              <span className={styles.emoji} aria-hidden="true">
                {option.emoji}
              </span>
              {isSelected && (
                <span className={styles.label}>
                  {option.label}
                </span>
              )}
            </button>
          );
        })}
      </div>
    );
  }
);

EmojiRating.displayName = 'EmojiRating';
